import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faCheck, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
	isOfflineSupported,
	getCachedUrls,
	cacheTracks,
	clearTracks,
} from "../serviceWorkerRegistration";

// Lets the listener pull the whole album down once (on wifi) so later plays
// cost no data at all. Tracks are also cached lazily as they are played; this
// just makes it deliberate and visible.
const OfflineToggle = ({ songs }) => {
	const urls = songs.map((s) => s.audio);
	// Stable primitive so the effect below re-runs when the track list actually
	// changes, not on every render (songs is rebuilt whenever `active` flips).
	const urlsKey = urls.join("|");
	const [supported] = useState(isOfflineSupported);
	const [cachedCount, setCachedCount] = useState(0);
	const [status, setStatus] = useState("idle"); // idle | working | error
	const [progress, setProgress] = useState(0);
	const [message, setMessage] = useState("");

	const refresh = useCallback(() => {
		getCachedUrls(urlsKey.split("|")).then((set) => setCachedCount(set.size));
	}, [urlsKey]);

	useEffect(() => {
		if (!supported) return;
		refresh();
	}, [supported, refresh]);

	if (!supported) return null;

	const total = urls.length;
	const allCached = cachedCount === total && total > 0;

	const handleDownload = () => {
		setStatus("working");
		setProgress(cachedCount);
		setMessage("");
		cacheTracks(urls, {
			onProgress: (done) => setProgress(done),
			onDone: () => {
				setStatus("idle");
				refresh();
			},
			onError: (err) => {
				setStatus("error");
				setMessage(err.message);
				refresh();
			},
		});
	};

	const handleClear = () => {
		setStatus("working");
		clearTracks({
			onDone: () => {
				setStatus("idle");
				setProgress(0);
				refresh();
			},
			onError: (err) => {
				setStatus("error");
				setMessage(err.message);
			},
		});
	};

	return (
		<Container>
			{status === "working" ? (
				<Button disabled>
					<Spinner icon={faSpinner} /> 저장 중 {progress}/{total}
				</Button>
			) : allCached ? (
				<Button onClick={handleClear} title="기기에서 저장된 음원을 삭제합니다">
					<FontAwesomeIcon icon={faCheck} /> 오프라인 저장됨
					<Trash icon={faTrash} />
				</Button>
			) : (
				<Button onClick={handleDownload} title="Wi-Fi에서 한 번 받아두면 이후 재생에 데이터가 들지 않습니다">
					<FontAwesomeIcon icon={faDownload} /> 오프라인 저장
					{cachedCount > 0 && ` (${cachedCount}/${total})`}
				</Button>
			)}
			{status === "error" && <Error>{message}</Error>}
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.4rem;
	padding: 0.5rem 1rem 1.5rem 1rem;
`;

const Button = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	background: transparent;
	border: 2px solid rgb(65, 65, 65);
	color: rgb(65, 65, 65);
	padding: 0.5rem 0.9rem;
	font-size: 0.85rem;
	font-family: inherit;
	cursor: pointer;
	transition: all 0.3s ease;
	&:hover:not(:disabled) {
		background: rgb(65, 65, 65);
		color: white;
	}
	&:disabled {
		cursor: default;
		opacity: 0.6;
	}
`;

const Trash = styled(FontAwesomeIcon)`
	opacity: 0.5;
	margin-left: 0.2rem;
`;

const Spinner = styled(FontAwesomeIcon)`
	animation: spin 1s linear infinite;
	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

const Error = styled.p`
	font-size: 0.7rem;
	color: rgb(180, 70, 70);
	text-align: center;
	max-width: 20rem;
`;

export default OfflineToggle;
